import { matchChoice } from '@/speech/understand/match';
import { BUILD, FIX, SPIN, type Option } from './exercises';
import { ACTIVITY_NAMES, offers, type Activity } from './lessons';

/**
 * Everything the Tongue Lab asks the child, as a question and its options: what the answer
 * matcher and the language model get at run time, and what the answer set
 * (ml/experiments/lab_answers.py) and the model's training use, keyed by the set's `context`.
 */
export interface LabPrompt {
  question: string;
  options: readonly Option[];
}

export const QUIZ_PROMPT: LabPrompt = {
  question: 'Which one makes a strong r: number 1 or number 2?',
  options: [
    { id: '0', label: 'Number 1', says: ['one'] },
    { id: '1', label: 'Number 2', says: ['two'] },
  ],
};

export const NEXT_PROMPT: LabPrompt = {
  question: 'Ready for the next one?',
  options: [
    { id: 'next', label: 'Next', says: ['next'] },
    { id: 'again', label: 'Again', says: ['again'] },
  ],
};

/** After an activity: which of the two offered activities comes next. */
export function choosePrompt(done: Activity): LabPrompt {
  const [a, b] = offers(done);
  return {
    question: `What would you like next: ${ACTIVITY_NAMES[a].label}, or ${ACTIVITY_NAMES[b].label}?`,
    options: [
      { id: a, label: ACTIVITY_NAMES[a].label, says: [...ACTIVITY_NAMES[a].says, 'one'] },
      { id: b, label: ACTIVITY_NAMES[b].label, says: [...ACTIVITY_NAMES[b].says, 'two'] },
    ],
  };
}

const ask = (item: (typeof BUILD)[number] | undefined): LabPrompt => ({
  question: item?.question ?? '',
  options: item?.options ?? [],
});

/**
 * The answer set's contexts. Its labels are the options' ids, except in the quiz (one, two;
 * `CONTEXT_LABELS` maps them). Its choice of activity is between Build the r and Spot the mistake,
 * one pair the app can offer.
 */
export const ANSWER_CONTEXTS: Readonly<Record<string, LabPrompt>> = {
  middle: ask(BUILD[0]),
  touch: ask(BUILD[1]),
  sides: ask(BUILD[2]),
  tip: ask(BUILD[3]),
  lips: ask(BUILD[4]),
  part: ask(FIX[0]),
  yesno: ask(SPIN[0]),
  number: QUIZ_PROMPT,
  next: NEXT_PROMPT,
  choose: {
    question: 'What would you like next: Build the r, or Spot the mistake?',
    options: [
      {
        id: 'build',
        label: ACTIVITY_NAMES.build.label,
        says: [...ACTIVITY_NAMES.build.says, 'one'],
      },
      { id: 'fix', label: ACTIVITY_NAMES.fix.label, says: [...ACTIVITY_NAMES.fix.says, 'two'] },
    ],
  },
};

/** The answer set's label for an option, where it differs from the option's id. */
export const CONTEXT_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  number: { one: '0', two: '1' },
};

/** The option id the set's label stands for in a context. */
export function optionFor(context: string, label: string): string {
  return CONTEXT_LABELS[context]?.[label] ?? label;
}

/** The rule-based pick for a prompt (null: undecided). */
export function rulePick(prompt: LabPrompt, text: string): string | null {
  return matchChoice(text, prompt.options);
}
